import React, { ComponentType } from "react"
import { NodeProps, useReactFlow } from "reactflow"
import { Grid, Typography } from "@material-ui/core"
import { themeColors } from "letalkui"

import { convertCentsInReals } from "@/utils/currencyFormatter"

import BaseBlock from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BaseBlock"
import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import ContrastChip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ContrastChip"
import ElseConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ElseConditionSection"

import useChatBotFlowBlockDetails from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowBlockDetails"

import BoardCardFieldSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/BoardCardFieldBlock/BoardCardFieldSectionEditor"
import AIValueNotFoundConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/BoardCardFieldBlock/AIValueNotFoundConditionSection"

import { getConditionByValidationSlug, disconnectRuleFromFlow } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockRule"

import { BlockNodeData } from "@/protocols/chatBotFlow"
import { ChatBotFlowBlockContent, BoardCardFieldMode } from "@/protocols/chatBot"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/BoardCardFieldBlock/styles"

const BoardCardFieldBlock: ComponentType<NodeProps<BlockNodeData>> = (node) => {
	const chatBotFlowBlockDetails = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })
	const reactFlow = useReactFlow()

	const classes = useStyles()

	const sectionId = String(node.data.chatBotFlowBlockId)

	const contactValueData = chatBotFlowBlockDetails.content?.boardCardFieldData
	const isIAMode = contactValueData?.mode === "ia"
	const hasValueDefined = contactValueData?.valueInCents !== undefined && contactValueData?.valueInCents !== null

	const handleModeChange = (mode: BoardCardFieldMode) => {
		const isSwitchingAwayFromAI = isIAMode && mode === "manual"

		if (isSwitchingAwayFromAI) {
			const fallbackCondition = getConditionByValidationSlug("contact-value-extraction-found-value", chatBotFlowBlockDetails.nextChatBotFlowBlockRules)

			if (fallbackCondition) {
				disconnectRuleFromFlow(reactFlow, fallbackCondition.rule, chatBotFlowBlockDetails.changeRule)
			}
		}

		chatBotFlowBlockDetails.changeContent({
			boardCardFieldData: {
				...chatBotFlowBlockDetails.content?.boardCardFieldData,
				mode
			}
		})
	}

	const handleSave = (content: Partial<ChatBotFlowBlockContent>) => {
		chatBotFlowBlockDetails.changeContent(content)
	}

	const renderValueBadge = () => {
		if (isIAMode) {
			return (
				<ContrastChip
					title="Capturado da conversa por IA"
					backgroundColor={themeColors.colors.system.green[50]}
					style={{ color: themeColors.colors.success[800], fontWeight: 600 }}
				/>
			)
		}

		if (hasValueDefined) {
			return (
				<ContrastChip
					title={convertCentsInReals(contactValueData?.valueInCents as number)}
					backgroundColor={themeColors.colors.system.green[50]}
					style={{ color: themeColors.colors.success[800], fontWeight: 600 }}
				/>
			)
		}

		return (
			<ContrastChip
				title="Nenhum valor definido"
				backgroundColor={themeColors.colors.system.gray[100]}
			/>
		)
	}

	return (
		<BaseBlock node={node}>
			<BlockBodySection fullWidth>
				<BoardCardFieldSectionEditor
					sectionId={sectionId}
					onSave={handleSave}
					onModeChange={handleModeChange}
					chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
				>
					<Grid
						container
						direction="column"
						alignItems="flex-start"
						className={classes.valueContainer}
					>
						<Typography
							variant="caption"
							className={classes.valueLabel}
						>
							Valor a atribuir:
						</Typography>

						{renderValueBadge()}
					</Grid>
				</BoardCardFieldSectionEditor>
			</BlockBodySection>

			<ElseConditionSection
				nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
				text="Próximo passo"
			/>

			{isIAMode && (
				<AIValueNotFoundConditionSection
					nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
				/>
			)}
		</BaseBlock>
	)
}

export default BoardCardFieldBlock
